import React, {useState, useContext} from 'react'
import {useTranslation} from 'react-i18next'

import {
  NotificationContext,
  NotificationType,
} from '~/components/notifications/NotificationContext'
import {SideModal} from '~/components/general/modal/SideModal'
import {Container} from '~/components/general/container/Container'
import {Typography} from '~/components/general/typography/Typography'
import {TextArea} from '~/components/general/textArea/TextArea'
import {Button} from '~/components/general/button/Button'
import {
  useBlockPartnerServiceSlot,
  useUnblockPartnerServiceSlot,
} from '~/api/partnerAdminSlots'

type BlockSlotModalProps = {
  isVisible: boolean
  onClose: () => void
  slotId: string
  partnerServiceId: string
  isBlock: boolean
  onSuccess: () => void
}

export const BlockSlotModal = ({
  isVisible,
  onClose,
  slotId,
  partnerServiceId,
  isBlock,
  onSuccess,
}: BlockSlotModalProps) => {
  const {t} = useTranslation()
  const {addNotification} = useContext(NotificationContext)
  const [comment, setComment] = useState('')

  const blockMutation = useBlockPartnerServiceSlot(partnerServiceId, slotId)
  const unblockMutation = useUnblockPartnerServiceSlot(partnerServiceId, slotId)

  const mutation = isBlock ? blockMutation : unblockMutation
  const actionLabel = isBlock ? t('Block') : t('Unblock')

  const handleConfirm = () => {
    const trimmedComment = comment.trim()
    mutation.mutate(
      {comment: trimmedComment || undefined},
      {
        onSuccess: () => {
          addNotification(
            t('Slot {{action}} successfully', {
              action: actionLabel.toLowerCase(),
            }),
            NotificationType.Success,
          )
          setComment('')
          onSuccess()
        },
        onError: (error: Error) => {
          addNotification(error.message, NotificationType.Error)
        },
      },
    )
  }

  const handleCancel = () => {
    setComment('')
    onClose()
  }

  return (
    <SideModal
      isVisible={isVisible}
      onClose={handleCancel}
      header={
        <Typography size="lg" weight="medium">
          {t('{{action}} Slot', {action: actionLabel})}
        </Typography>
      }>
      <Container direction="column" spacing={2}>
        <Typography weight="bold">{t('Add a comment')}</Typography>
        <TextArea
          height="120px"
          placeholder={t('Write your reason (optional)')}
          value={comment}
          onChange={(e) => setComment(e.target.value)}
        />
        <Container flex direction="row" justify="flex-end" spacing={1}>
          <Button color="secondary" onClick={handleCancel} size="md">
            {t('Back')}
          </Button>
          <Button
            color="primary"
            onClick={handleConfirm}
            size="md"
            disabled={mutation.isPending}>
            {t('Confirm')}
          </Button>
        </Container>
      </Container>
    </SideModal>
  )
}
